import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, School, ShieldCheck } from 'lucide-react';

import DashboardLayout from '../components/Layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../utils/backendBaseUrl';
import { appPath } from '../utils/appRouting';

const API = getApiBaseUrl();

const ROLE_OPTIONS = [
  { id: 'docente_sostegno', label: 'Docente di sostegno' },
  { id: 'docente_curricolare', label: 'Docente curricolare' },
  { id: 'educatore', label: 'Educatore' },
  { id: 'dirigente', label: 'Dirigente' },
  { id: 'altro', label: 'Altro' }
];

const SCHOOL_LEVEL_OPTIONS = [
  { id: 'infanzia', label: 'Infanzia' },
  { id: 'primaria', label: 'Primaria' },
  { id: 'secondaria_1', label: 'Secondaria I grado' },
  { id: 'secondaria_2', label: 'Secondaria II grado' }
];

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshUserProfile } = useAuth();

  const email = useMemo(() => currentUser?.email || userProfile?.email || '', [currentUser?.email, userProfile?.email]);

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: userProfile?.nome || '',
    cognome: userProfile?.cognome || '',
    ruolo: userProfile?.ruolo || '',
    role: userProfile?.role || '',
    schoolLevel: userProfile?.schoolLevel || ''
  });

  useEffect(() => {
    // Se già completo, esci.
    const complete = userProfile?.profileComplete ?? userProfile?.profile_complete;
    if (complete === true) {
      navigate(appPath('/dashboard'), { replace: true });
    }
    // eslint-disable-next-line
  }, [userProfile]);

  const canSubmit =
    form.nome.trim().length > 0 &&
    form.cognome.trim().length > 0 &&
    (form.ruolo.trim().length > 0 || form.role.trim().length > 0) &&
    form.schoolLevel.trim().length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      toast.error('Devi effettuare l\'accesso');
      navigate(appPath('/login'));
      return;
    }
    if (!canSubmit) {
      toast.error('Completa tutti i campi obbligatori');
      return;
    }

    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        `${API}/auth/complete-profile`,
        {
          nome: form.nome.trim(),
          cognome: form.cognome.trim(),
          ruolo: form.ruolo.trim() || undefined,
          role: form.role.trim() || undefined,
          schoolLevel: form.schoolLevel.trim() || undefined
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );

      await refreshUserProfile?.();
      toast.success('Profilo completato');
      navigate(appPath('/dashboard'), { replace: true });
    } catch (err) {
      console.error('Complete profile error:', err);
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-border flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-lg font-semibold text-foreground">Completa il tuo profilo</div>
              <div className="text-sm text-muted-foreground">
                Ti serve per attivare l\'account e usare tutte le funzionalità.
              </div>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label>Email</Label>
                <Input value={email} disabled className="mt-1" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" /> Nome *
                  </Label>
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
                    className="mt-1"
                    placeholder="Nome"
                    required
                  />
                </div>
                <div>
                  <Label>Cognome *</Label>
                  <Input
                    value={form.cognome}
                    onChange={(e) => setForm((p) => ({ ...p, cognome: e.target.value }))}
                    className="mt-1"
                    placeholder="Cognome"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <School className="w-4 h-4" /> Ordine di scuola *
                  </Label>
                  <select
                    value={form.schoolLevel}
                    onChange={(e) => setForm((p) => ({ ...p, schoolLevel: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    required
                  >
                    <option value="">Seleziona...</option>
                    {SCHOOL_LEVEL_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Ruolo *</Label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm((p) => ({ ...p, role: e.target.value, ruolo: '' }))}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    required
                  >
                    <option value="">Seleziona...</option>
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="submit" disabled={!canSubmit || saving}>
                  {saving ? 'Salvataggio...' : 'Salva e continua'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
